import React, { useEffect, useMemo, useState } from 'react';
import { Panel } from '@/components/ui/panel';
import OutlinedButton from '@/components/common/buttons/OutlinedButton';
import PrimaryButton from '@/components/common/buttons/PrimaryButton';
import CancelOutlinedButton from '@/components/common/buttons/CancelOutlinedButton';
import { Box, HStack, Icon, Span, Text, VStack } from '@chakra-ui/react';
import RightSidePanel, { type RightSidePanelListNavigation } from '@/components/common/layout/RightSidePanel';
import DeleteConfirmationDialog from '@/components/common/DeleteConfirmationDialog';
import RecordCorrectionsSection from '@/components/documents/RecordCorrectionsSection';
import { OverflowActionItem, OverflowActionsMenu } from '@/components/common/OverflowActionsMenu';
import { FiCheck, FiDownload, FiEdit2, FiSend, FiTrash2, FiX } from 'react-icons/fi';
import { LuCircleAlert, LuFileQuestion, LuLock, LuLockOpen } from 'react-icons/lu';
import SharingVisibilitySwitch, { VisibilityCaptionVariant } from '@/components/documents/SharingVisibilitySwitch';
import { DocumentWithDownloadUrl as Document, PendingDocumentShareResponse } from '@/client';
import { toaster } from '@/components/ui/toaster.tsx';
import usePresignedDownloadUrl from '@/hooks/usePresignedDownloadUrl';
import { displayRecordName, isFile, isImportant, isLink, isNote } from '@/utils/documentUtils.ts';
import { ShowClosableNotification, showClosableNotification } from '@/config/notifications.ts';
// Import specialized document edit components
import DocumentFileEdit from './edit/DocumentFileEdit';
import DocumentNoteEdit from './edit/DocumentNoteEdit';
import DocumentNoteSheet from './edit/DocumentNoteSheet';
import DocumentLinkEdit from './edit/DocumentLinkEdit';
import CriticalInfoEdit from './edit/CriticalInfoEdit';
import useHouseRecords from '@/hooks/house/documents/useHouseRecords.ts';
import { useHousePermissions } from '@/hooks/useHasPermission';
import { useUpdatePendingShareStatus } from '@/hooks/documents/useUpdatePendingShareStatus';
import { useShareDocumentsWithOwner } from '@/hooks/documents/useShareDocumentsWithOwner';
import { getUserDisplayName } from '@/utils/userDisplayUtils';
import { DocumentReadinessState, useDocumentStatus } from '@/hooks/house/documents/useDocumentStatus';
import { Tooltip } from '@/components/ui/tooltip';

interface DownloadResponse {
	download_url: string;
}

const EMPTY_CITED_PAGES: number[] = [];

interface RecordEditDrawerProps {
	document: Document;
	isOpen: boolean;
	onOpenChange: (state: { open: boolean }) => void;
	/** Fires once the panel has animated off screen, for a caller that unmounts it on close. */
	onExitComplete?: () => void;
	onDelete?: (document: Document, showNotification?: ShowClosableNotification) => Promise<void>;
	pendingDocument?: PendingDocumentShareResponse;
	/** Hide the footer actions (delete/download/edit) — used when opened read-only from a chat answer. */
	hideFooter?: boolean;
	/** Cited pages when this drawer is opened from an assistant citation. */
	citedPages?: number[];
	/** Archive copy: this record is eligible to submit to the current owner (post-transfer, not yet sent). */
	canSendToOwner?: boolean;
	/** Archive copy: this record has already been submitted to the current owner. */
	isSentToOwner?: boolean;
	listNavigation?: RightSidePanelListNavigation;
}

/**
 * Component for viewing and editing document details in a drawer
 */
const RecordEditDrawer: React.FC<RecordEditDrawerProps> = ({
	document,
	isOpen,
	onOpenChange,
	onDelete,
	pendingDocument,
	hideFooter = false,
	citedPages = EMPTY_CITED_PAGES,
	canSendToOwner = false,
	isSentToOwner = false,
	listNavigation,
	onExitComplete,
}) => {
	// Derive values from pendingDocument
	const isPending = !!pendingDocument;
	const { readinessState } = useDocumentStatus(isPending ? null : document.id, document.uploaded_at);
	const processingFailed = readinessState === DocumentReadinessState.Failed;
	const processingStalled = readinessState === DocumentReadinessState.Stalled;
	const pendingShareId = pendingDocument?.id;
	// TODO: Check backend pending-share serialization. shared_by.name can be empty, so this falls
	// back to email even when the sender may have a profile name.
	const sharedByName = getUserDisplayName(pendingDocument?.shared_by ?? null);
	const { refetch } = useHouseRecords(document.house_id);
	const [isDownloading, setIsDownloading] = useState(false);
	const [optimisticPrivate, setOptimisticPrivate] = useState(document.private ?? false);
	const displayDocument = useMemo(() => ({ ...document, private: optimisticPrivate }), [document, optimisticPrivate]);

	useEffect(() => {
		setOptimisticPrivate(document.private ?? false);
	}, [document.id, document.private]);

	// Use the new hook signature - only fetch when downloading
	const { refetch: fetchDownloadUrl } = usePresignedDownloadUrl(
		document.id,
		false, // Don't fetch automatically
	);

	// Pending share status update hook
	const { updateStatus } = useUpdatePendingShareStatus(document.house_id);
	// Send-to-owner (archive copy) — submit this record to the current owner.
	const { shareDocuments } = useShareDocumentsWithOwner(document.house_id);
	const [isSendingToOwner, setIsSendingToOwner] = useState(false);
	// Privacy chosen for the pushed copy at accept time. The share doc isn't in this house yet,
	// so this rides on the accept payload, not a visibility PATCH.
	const [acceptPrivate, setAcceptPrivate] = useState(false);
	const panelIsPrivate = isPending ? acceptPrivate : displayDocument.private;

	const showDeleteButton = !isImportant(document) && !isPending;
	const showDownloadButton = isFile(document);
	const showEditButton = isNote(document) && !isPending;
	// Whether Download is the only verb in the footer, so it takes the full width with its label
	// rather than collapsing to an icon. The overflow menu sits beside it either way and is not a
	// verb, so it does not count here — but both controls are `lg`, or the row looks misaligned.
	const downloadIsOnlyFooterVerb = showDownloadButton && !showEditButton && !canSendToOwner && !isSentToOwner;

	// Delete document mutation state
	const [isDeleting, setIsDeleting] = useState(false);
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
	const [isNoteSheetOpen, setIsNoteSheetOpen] = useState(false);

	const handleCriticalInfoUpdate = async () => {
		onOpenChange({ open: false });

		// Refetch the records to ensure the latest data is displayed
		await refetch();
	};

	const onTextRecordUpdated = () => {
		// Note: there is no need to refetch here as the update task record hook handles it
		onOpenChange({ open: false });
	};

	const handleDownload = async () => {
		try {
			setIsDownloading(true);
			const result = await fetchDownloadUrl();
			if (result.data) {
				const downloadData = result.data as DownloadResponse;
				window.open(downloadData.download_url, '_blank');
			}
		} catch (error) {
			console.error('Download failed:', error);
			toaster.create({
				description: 'Failed to download document',
				type: 'error',
				closable: true,
			});
		} finally {
			setIsDownloading(false);
		}
	};

	const handleConfirmDelete = async () => {
		if (!onDelete) return;

		try {
			setIsDeleting(true);
			setIsDeleteDialogOpen(false);
			onOpenChange({ open: false });
			await onDelete(document, showClosableNotification);
		} catch (error) {
			toaster.create({
				description: `Failed to delete document ${displayRecordName(document)}`,
				type: 'error',
				closable: true,
			});
			console.error('Delete error:', error);
		} finally {
			setIsDeleting(false);
		}
	};

	const handleCancelDelete = () => {
		setIsDeleteDialogOpen(false);
	};

	// Accept/decline optimistically: close the panel immediately (so it animates shut on click) and run
	// the status update in the background. The record materialises / drops out of the list when it lands;
	// on failure we surface a toast and the record stays pending for another try.
	const handleAccept = () => {
		if (!pendingShareId) return;
		const shareId = pendingShareId;
		const isPrivate = acceptPrivate;
		onOpenChange({ open: false });
		void updateStatus(shareId, 'accepted', undefined, isPrivate).catch((error) => {
			console.error('Failed to accept document:', error);
			toaster.create({ description: 'Failed to accept document', type: 'error', closable: true });
		});
	};

	const handleReject = () => {
		if (!pendingShareId) return;
		const shareId = pendingShareId;
		onOpenChange({ open: false });
		void updateStatus(shareId, 'rejected').catch((error) => {
			console.error('Failed to reject document:', error);
			toaster.create({ description: 'Failed to reject document', type: 'error', closable: true });
		});
	};

	const handleSendToOwner = async () => {
		if (!canSendToOwner || isSendingToOwner) return;
		setIsSendingToOwner(true);
		try {
			await shareDocuments([document.id]);
			onOpenChange({ open: false });
		} catch (error) {
			console.error('Failed to send record to owner:', error);
			toaster.create({ description: 'Failed to send record to owner', type: 'error', closable: true });
		} finally {
			setIsSendingToOwner(false);
		}
	};

	const panelTitle = (
		<Box color="brand.fg" px={6} pt={6} pb={4} w="100%">
			<Text fontSize="2xl" fontWeight="semibold" lineHeight="1.1" whiteSpace="normal" wordBreak="break-word">
				{displayRecordName(displayDocument)}
			</Text>
		</Box>
	);

	const { canDeleteDocuments: canManageDocuments } = useHousePermissions();

	// For pending documents, show Accept/Reject actions
	const pendingDocumentsActions = (
		<VStack w="100%" gap={0} borderTop="1px solid" borderColor="border.default" pt={4}>
			<VStack align="stretch" w="100%" gap={3} p={4}>
				<Text fontSize="xl" fontWeight="semibold" lineHeight="1.2" color="brand.fg">
					Update offer
				</Text>
				<HStack gap={4} w="100%" align="flex-start">
					<Box alignSelf="start">
						<Icon as={LuFileQuestion} boxSize={5} color="brand.fg" />
					</Box>
					{sharedByName && (
						<Text fontSize="md">
							<Span fontWeight="semibold">{sharedByName}</Span> sent this record update for your review.
						</Text>
					)}
				</HStack>
			</VStack>
			<Box w="100%" pb={4}>
				<Panel px={4} py={4}>
					<SharingVisibilitySwitch
						showTitle
						layout="inline"
						titleContent={
							<HStack gap={3}>
								<Icon as={acceptPrivate ? LuLock : LuLockOpen} boxSize={5} color="brand.fg" />
								<Text textStyle="body.strong" color="brand.fg">
									Set privacy
								</Text>
							</HStack>
						}
						captionVariant={VisibilityCaptionVariant.PendingRecord}
						isPrivate={acceptPrivate}
						onValueChange={setAcceptPrivate}
					/>
				</Panel>
			</Box>
			<HStack gap={4} w="100%">
				<CancelOutlinedButton
					size="lg"
					textStyle="label"
					onClick={handleReject}
					disabled={!pendingShareId}
					flex="1"
				>
					<FiX />
					Decline
				</CancelOutlinedButton>
				<PrimaryButton size="lg" textStyle="label" onClick={handleAccept} disabled={!pendingShareId} flex="1">
					<FiCheck />
					Accept
				</PrimaryButton>
			</HStack>
		</VStack>
	);
	/**
	 * The overflow leads the footer, as the contact and maintenance drawers' do, rather than sitting
	 * in the header: the footer is where the drawer's verbs are, and a menu of more of them belongs
	 * beside them.
	 *
	 * The overflow exists to hold Delete, so without a way to delete there is no overflow.
	 *
	 * It used to render regardless and grey the item out, which offered the action, refused it, and
	 * gave no reason — and wore Chakra's disabled cursor while doing it. A caller that passes no
	 * `onDelete` is not withholding permission, it has no deletion to wire up: the review pass opens
	 * a record to read it. Nothing to perform, nothing to show.
	 *
	 * `canManageDocuments` and `!isPending` are already true wherever this renders — the footer
	 * branch below is gated on the first, and `showDeleteButton` carries the second.
	 */
	const overflowActions =
		showDeleteButton && onDelete ? (
			<OverflowActionsMenu
				ariaLabel="More record actions"
				tooltip="More actions"
				size="lg"
				triggerProps={{ boxShadow: 'sm', _hover: { bg: 'surface.subtle', color: 'brand.fg' } }}
			>
				<OverflowActionItem
					icon={FiTrash2}
					destructive
					disabled={isDeleting}
					onClick={() => setIsDeleteDialogOpen(true)}
				>
					Delete
				</OverflowActionItem>
			</OverflowActionsMenu>
		) : null;

	const panelFooter = canManageDocuments ? (
		isPending ? (
			pendingDocumentsActions
		) : (
			<VStack w="100%" gap={0}>
				<HStack gap={4} w="100%" mb={4}>
					{overflowActions}
					{showDownloadButton && (
						<Tooltip content="Download record" openDelay={300}>
							<OutlinedButton
								elevated
								size="lg"
								loading={isDownloading}
								onClick={handleDownload}
								flex={{
									base: downloadIsOnlyFooterVerb ? '1' : '0 0 var(--chakra-sizes-11)',
									sm: '1',
								}}
								w={{ base: downloadIsOnlyFooterVerb ? 'full' : 11, sm: 'auto' }}
								minW={{ base: downloadIsOnlyFooterVerb ? 0 : 11, sm: '96px' }}
								px={{ base: downloadIsOnlyFooterVerb ? 4 : 0, sm: 4 }}
								boxShadow={downloadIsOnlyFooterVerb ? 'sm' : undefined}
								aria-label="Download record"
								_hover={{ bg: 'brand.solid', color: 'brand.contrast' }}
							>
								<FiDownload />
								<Text
									as="span"
									display={{ base: downloadIsOnlyFooterVerb ? 'inline' : 'none', sm: 'inline' }}
								>
									Download
								</Text>
							</OutlinedButton>
						</Tooltip>
					)}
					{showEditButton && (
						<OutlinedButton
							elevated
							size="lg"
							onClick={() => setIsNoteSheetOpen(true)}
							flex="1"
							_hover={{ bg: 'brand.solid', color: 'brand.contrast' }}
						>
							<FiEdit2 /> Edit
						</OutlinedButton>
					)}
					{canSendToOwner && (
						<PrimaryButton
							size="lg"
							fontSize="sm"
							loading={isSendingToOwner}
							onClick={handleSendToOwner}
							flex="1"
						>
							<FiSend /> Send to owner
						</PrimaryButton>
					)}
					{!canSendToOwner && isSentToOwner && (
						<HStack flex="1" justify="center" gap={2} color="fg.muted" fontSize="sm">
							<Icon as={FiCheck} />
							<Span>Sent to owner</Span>
						</HStack>
					)}
				</HStack>
			</VStack>
		)
	) : null;

	// Render the appropriate component based on document type
	const renderDocumentContent = () => {
		if (isNote(displayDocument)) {
			return (
				<DocumentNoteEdit
					document={displayDocument}
					onVisibilityChange={setOptimisticPrivate}
					isPendingShare={isPending}
				/>
			);
		}

		if (isLink(displayDocument)) {
			return (
				<DocumentLinkEdit
					document={displayDocument}
					onDocumentUpdated={onTextRecordUpdated}
					onVisibilityChange={setOptimisticPrivate}
					isPendingShare={isPending}
				/>
			);
		}

		if (isImportant(displayDocument)) {
			return (
				<CriticalInfoEdit
					document={displayDocument}
					onDocumentUpdated={handleCriticalInfoUpdate}
					onVisibilityChange={setOptimisticPrivate}
				/>
			);
		}

		// Check if it's a file document or use as default fallback
		if (isFile(displayDocument)) {
			return (
				<DocumentFileEdit
					document={displayDocument}
					citedPages={citedPages}
					onVisibilityChange={setOptimisticPrivate}
					isPendingShare={isPending}
				/>
			);
		}

		// Fallback for any unrecognized document type
		return (
			<DocumentFileEdit
				document={displayDocument}
				citedPages={citedPages}
				onVisibilityChange={setOptimisticPrivate}
				isPendingShare={isPending}
			/>
		);
	};

	return (
		<>
			<RightSidePanel
				isOpen={isOpen}
				onOpenChange={onOpenChange}
				onExitComplete={onExitComplete}
				title={isNote(displayDocument) ? undefined : panelTitle}
				footer={hideFooter ? undefined : panelFooter}
				showCloseButton
				showNavigation
				listNavigation={listNavigation}
				contentProps={panelIsPrivate ? { bg: 'bg.muted' } : undefined}
				overlay={
					isNote(displayDocument) ? (
						<DocumentNoteSheet
							document={displayDocument}
							isOpen={isNoteSheetOpen}
							onOpenChange={setIsNoteSheetOpen}
						/>
					) : null
				}
			>
				<VStack align="stretch" gap={4} w="full">
					{(processingFailed || processingStalled) && (
						<VStack layerStyle="card" align="stretch" gap={1.5} p={3}>
							<HStack gap={2} color={processingStalled ? 'orange.600' : 'text.error'}>
								<Icon as={LuCircleAlert} boxSize={5} flexShrink={0} />
								<Text textStyle="body.strong" lineHeight="1.5">
									{processingStalled ? 'Processing has stalled' : 'Record could not be processed'}
								</Text>
							</HStack>
							<Text fontSize="sm" color="fg.muted" lineHeight="tall">
								{processingStalled
									? 'Please delete this file and re-upload it to retry.'
									: "This record won't be included in answers. It's been flagged for our team to review—no action is needed from you."}
							</Text>
							{processingStalled && canManageDocuments && showDeleteButton && onDelete && (
								<OutlinedButton
									size="sm"
									alignSelf="start"
									mt={1}
									color="text.error"
									_hover={{ bg: 'red.50', borderColor: 'red.300' }}
									onClick={() => setIsDeleteDialogOpen(true)}
								>
									<FiTrash2 />
									Delete record
								</OutlinedButton>
							)}
						</VStack>
					)}
					{renderDocumentContent()}
					<RecordCorrectionsSection document={displayDocument} />
				</VStack>
			</RightSidePanel>

			<DeleteConfirmationDialog
				isOpen={isDeleteDialogOpen}
				onOpenChange={setIsDeleteDialogOpen}
				title="Delete record?"
				// Deleting a record rebuilds the plan from the documents left, so tasks drawn only from
				// this one go with it. Nothing else on the way to this button suggests a record is
				// holding anything up.
				message="Related tasks will be removed too."
				itemName={displayRecordName(displayDocument)}
				confirmText="Delete"
				cancelText="Cancel"
				onConfirm={handleConfirmDelete}
				onCancel={handleCancelDelete}
				isLoading={isDeleting}
				loadingText="Deleting..."
			/>
		</>
	);
};

export default RecordEditDrawer;
